import React, { useState, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  LayoutDashboard, Inbox, Users, UserCheck,
  UserCog, FolderOpen, TrendingUp, Cog, Search,
  Menu, X, Shield, Calendar, Briefcase
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useLayout } from '../../contexts/LayoutContext';
import { useTheme } from '../../contexts/ThemeContext';
import '../../styles/sidebar-animated.css';

interface NavItem {
  id: string;
  label: string;
  path: string;
  icon: any;
  badge?: number | string;
  roles?: string[];
}

const navItems: NavItem[] = [
  { id: 'dashboard', label: 'Dashboard', path: '/dashboard', icon: LayoutDashboard },
  { id: 'email', label: 'E-Mail', path: '/email', icon: Inbox },
  { id: 'calendar', label: 'Kalender', path: '/calendar-tasks', icon: Calendar },
  { id: 'companies', label: 'Unternehmen', path: '/companies', icon: Users },
  { id: 'recruiting', label: 'Kandidaten', path: '/recruiting', icon: UserCheck },
  { id: 'job-orders', label: 'Stellenaufträge', path: '/job-orders', icon: Briefcase },
  { id: 'employees', label: 'Mitarbeiter', path: '/employees', icon: UserCog },
  { id: 'documents', label: 'Dokumente', path: '/documents', icon: FolderOpen },
  { id: 'analytics', label: 'Analytics', path: '/analytics', icon: TrendingUp },
  { id: 'settings', label: 'Einstellungen', path: '/settings', icon: Cog },
];

export const SidebarAnimated: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const location = useLocation();
  const { user } = useAuth();
  const { setSidebarCollapsed } = useLayout();
  const { theme: _theme } = useTheme();

  // Check if user is Super Admin
  const isSuperAdmin = user?.email === 'info@phe-perm.de' ||
                      user?.role === 'SUPER_ADMIN';

  // Get icon color for light theme
  const iconColor = '#1f2937'; // dark gray for light mode

  // Update layout context based on hover state
  useEffect(() => {
    setSidebarCollapsed(!isHovered);
    // Update the main content margin through CSS
    document.documentElement.style.setProperty('--sidebar-width', isHovered ? '280px' : '80px');
  }, [isHovered, setSidebarCollapsed]);

  // Close mobile sidebar on route change
  useEffect(() => {
    setIsMobileOpen(false);
  }, [location.pathname]);

  // Add Super Admin item if user is Super Admin
  const allNavItems = [...navItems];
  if (isSuperAdmin) {
    // Insert after dashboard
    allNavItems.splice(1, 0, {
      id: 'super-admin',
      label: 'Super Admin',
      path: '/super-admin',
      icon: Shield,
      badge: 'ADMIN',
      roles: ['SUPER_ADMIN']
    });
  }

  const filteredNavItems = allNavItems.filter(item => {
    // Role-based filtering
    if (item.roles && !item.roles.includes(user?.role || '') && !isSuperAdmin) {
      return false;
    }
    return true;
  });

  return (
    <>
      {/* Mobile Menu Toggle */}
      <button
        className="mobile-menu-toggle"
        onClick={() => setIsMobileOpen(!isMobileOpen)}
        style={{
          display: 'none',
          position: 'fixed',
          top: 'var(--spacing-md)',
          left: 'var(--spacing-md)',
          zIndex: 101,
          padding: 'var(--spacing-sm)',
          background: 'var(--glass)',
          border: '1px solid var(--glass-border)',
          borderRadius: 'var(--radius-md)',
          cursor: 'pointer',
          color: iconColor
        }}
      >
        {isMobileOpen ? <X size={20} /> : <Menu size={20} />}
      </button>

      <aside
        className={`sidebar-animated ${isMobileOpen ? 'mobile-open' : ''}`}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        style={{
          width: isHovered ? 280 : 80  // Increased from 60 to 80 to prevent icon cutoff
        }}
      >
        {/* Logo & Company Selector */}
        <div className="sidebar-header">
          <div className="logo-container">
            <div className="logo" style={{
              minWidth: 44, // Ensure logo doesn't shrink
              width: 44,
              height: 44
            }}>
              HR
            </div>
            <div className="sidebar-text">
              <h3 style={{ fontSize: '1.125rem', fontWeight: 600 }}>CRM Pro</h3>
              <p style={{ fontSize: '0.75rem', color: 'var(--text-tertiary)' }}>
                PHE Perm
              </p>
            </div>
          </div>

          {/* Search */}
          <div className="search-container">
            <div style={{ position: 'relative' }}>
              <Search
                size={16}
                style={{
                  position: 'absolute',
                  left: 12,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: 'var(--text-tertiary)'
                }}
              />
              <input
                type="text"
                placeholder="Suchen..."
                className="input-field"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{
                  width: '100%',
                  paddingLeft: 36
                }}
              />
            </div>
          </div>
        </div>

        {/* Navigation Items */}
        <nav style={{
          flex: 1,
          padding: '0 var(--spacing-sm)',
          overflowY: 'auto',
          overflowX: 'hidden'  // Prevent horizontal overflow
        }}>
          {filteredNavItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;

            return (
              <NavLink
                key={item.id}
                to={item.path}
                className={`nav-item ${isActive ? 'active' : ''}`}
                data-tooltip={item.label}
                style={{
                  color: isActive ? 'var(--accent-primary)' : iconColor,
                  minHeight: 44 // Ensure minimum height for touch targets
                }}
              >
                <span className="nav-item-icon" style={{
                  minWidth: 40,  // Increased from 32 to prevent cutoff
                  width: 40,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Icon
                    size={20}
                    className={isActive ? 'icon-active' : ''}
                  />
                </span>
                <span className="nav-item-text">
                  {item.label}
                </span>
                {item.badge && (
                  <span className="nav-item-badge">
                    {item.badge}
                  </span>
                )}
                {isActive && (
                  <div className="active-indicator" />
                )}
              </NavLink>
            );
          })}
        </nav>

      </aside>

      {/* Mobile Overlay */}
      {isMobileOpen && (
        <div
          className="mobile-overlay"
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            zIndex: 99
          }}
          onClick={() => setIsMobileOpen(false)}
        />
      )}
    </>
  );
};